import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Site-wide scroll reset on route change.
 *
 * React Router's <BrowserRouter> is a client-side SPA — navigating between
 * routes only swaps the rendered component tree without triggering a
 * browser-level page load, so the window keeps whatever scroll position the
 * user was at on the previous page. On mobile especially, this makes a click
 * from a footer link feel broken (the new page opens "already scrolled
 * halfway down").
 *
 * This component mounts once at the router root, watches `pathname` (and
 * `search`, so `?category=…` filter changes on the archive page also reset),
 * and imperatively scrolls the window to the top on every change.
 *
 * We intentionally skip hash navigations (e.g. anchor links) — those should
 * keep default browser behaviour.
 */
const ScrollToTop = () => {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if (hash) return; // let in-page anchor jumps behave naturally
    // Instant scroll — a smooth animation here would collide with the fade
    // page-transition and feel sluggish. Users perceive the transition as
    // the "movement" cue; the actual scroll reset should be instant.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, search, hash]);

  return null;
};

export default ScrollToTop;
